import classNames from 'classnames';
import i18n from 'src/utils/translate';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import Button from 'src/components/buttons/button';
import footfallEventTypes from 'src/constants/footfallEventTypes';
import signupContext from 'src/constants/signupContext';
import { followCommunity, unfollowCommunity } from 'src/modules/community/shared/actions';
import { followPost, unfollowPost } from 'src/modules/community/singlePost/actions/actions';
import { showModal, showSignupModal } from 'src/modules/shared/modal/actions';
import ModalType from 'src/constants/modalTypes';
import { trackFootfallEvent } from 'src/utils/footfall';
import FollowSuccessMessage from 'src/modules/community/shared/components/alert/followSuccessMessage';
import { getShowSavePostModalPreference } from 'src/utils/showSavePostModalPreference';

class FollowButton extends React.Component {

  static propTypes = {
    buttonText: PropTypes.string,
    buttonType: PropTypes.string,
    className: PropTypes.string,
    community: PropTypes.object,
    followIds: PropTypes.array,
    followType: PropTypes.string,
    fullWidth: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isFollowing: PropTypes.bool,
    noIcon: PropTypes.bool,
    role: PropTypes.string,
    signupContext: PropTypes.object,
    signupSuccess: PropTypes.func,
    tabIndex: PropTypes.string,
    trackLabel: PropTypes.string,
    user: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.bool,
    ]),
  }

  static defaultProps = {
    buttonText: null,
    buttonType: 'secondary',
    className: '',
    community: {}, // Group object from api
    followIds: [],
    followType: 'user',
    fullWidth: false,
    isDisabled: false,
    isFollowing: false,
    noIcon: false,
    role: '',
    signupContext: {
      element: signupContext.element.FOLLOW,
      section: signupContext.section.UNTRACKED,
    },
    signupSuccess: () => {},
    tabIndex: '',
    trackLabel: 'N/A',
    user: {},
  }

  constructor(props) {
    super(props);
    this.state = {hovered: false};
  }

  triggerGroupLeavingModal = () => {
    const successMessage = i18n.t('You have left %1$s').replace('%1$s', this.props.community.name) + '!';
    this.props.showModal({
      modalProps: {
        title: 'Are you sure you want to do this?',
        cancel: i18n.t('Cancel'),
        accept: i18n.t('Leave Community'),
        onAccept: () => this.props.unfollowCommunity(this.props.community, this.props.user.userId, successMessage),
        children: i18n.t('Leaving the community will affect your news feed, emails, notifications and badges. Your contributions will remain on the community unless you delete them.'),
      },
    });
  }

  onClick = () => {
    const {followType, community, isActive, user, trackLabel, signupContext, showSignupModal, signupSuccess, showModal} = this.props;

    if (!isActive) {
      showSignupModal(
        this.props.signupContext,
        followType === 'group' ? community : null,
        false,
        null,
        signupSuccess,
      );
      trackFootfallEvent(footfallEventTypes.CLICKED, {
        clickType: 'sign-up',
        clickMetadata: { ...signupContext },
      });
    } else if (followType === 'post') {
      if (this.props.isFollowing) {
        this.props.unfollowPost(this.props.followIds[0], i18n.t('Post Unsaved!'));
      } else {
        this.props.followPost(this.props.followIds[0], trackLabel);
        if (getShowSavePostModalPreference()) {
          showModal({ modalType: ModalType.SAVE_POST });
        }
      }
    }
    else if (followType === 'group'){
      if (this.props.isFollowing) {
        this.triggerGroupLeavingModal();
      } else {
        this.props.followCommunity(community, user.userId, <FollowSuccessMessage communityName={community.name}/>);
      }
    }
  }

  debouncedClick = debounce(this.onClick, 300);

  getIcon(isFollowing, hovered, noIcon) {
    if (noIcon) return '';

    if (!isFollowing){
      return 'plus';
    } else {
      if (hovered) {
        return 'cancel';
      }
      return 'ok';
    }
  }

  render() {
    const { fullWidth, isFollowing, isDisabled, noIcon, size, className, buttonType,
      followType, id, children, buttonText, tabIndex, role } = this.props;

    const iconClasses = {
      'icon--success': true,
      'icon--danger': isFollowing && this.state.hovered && !noIcon,
    };

    const text = buttonText || i18n.t('Join');

    let followingText = noIcon || this.state.hovered ? i18n.t('Leave') : i18n.t('');
    let followingAriaLabel = i18n.t('Leave community');
    if (followType === 'post') {
      followingText = this.state.hovered ? i18n.t('Unsave') : i18n.t('Saved');
      followingAriaLabel = i18n.t('Unsave post');
    }

    return (
      <Button
        ariaLabel={isFollowing ? followingAriaLabel : text}
        className={className}
        dataTestId="follow-button"
        disabled={isDisabled}
        fullWidth={fullWidth}
        icon={this.getIcon(isFollowing, this.state.hovered, noIcon)}
        iconClassName={classNames(iconClasses)}
        id={id}
        role={role}
        size={size}
        tabIndex={tabIndex}
        text={isFollowing ? followingText : children || text}
        type={buttonType || 'secondary'}
        onClick={this.debouncedClick}
        onMouseEnter={() => this.setState({hovered: true})}
        onMouseLeave={() => this.setState({hovered: false})}
      />
    );
  }
}

const actions = {
  followPost,
  unfollowPost,
  followCommunity,
  unfollowCommunity,
  showSignupModal,
  showModal,
};

export default connect(null, actions)(FollowButton);
